import { Quotation, Template } from './types';
import { DEFAULT_TEMPLATES } from './templates';

// Backend integration point: replace localStorage with API calls
const STORAGE_KEY = 'tgi_quotations';
const SEQUENCE_KEY = 'tgi_quote_sequence';
const TEMPLATES_KEY = 'tgi_templates';
const TEMPLATES_INITIALIZED_KEY = 'tgi_templates_initialized';

export function getQuotations(): Quotation[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveQuotations(quotations: Quotation[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(quotations));
}

export function getQuotationById(id: string): Quotation | null {
  const quotations = getQuotations();
  return quotations.find((q) => q.id === id) || null;
}

export function upsertQuotation(quotation: Quotation): void {
  const quotations = getQuotations();
  const idx = quotations.findIndex((q) => q.id === quotation.id);
  if (idx >= 0) {
    quotations[idx] = { ...quotation, updatedAt: new Date().toISOString() };
  } else {
    quotations.unshift(quotation);
  }
  saveQuotations(quotations);
}

export function deleteQuotation(id: string): void {
  const quotations = getQuotations().filter((q) => q.id !== id);
  saveQuotations(quotations);
}

export function generateQuoteNumber(): string {
  if (typeof window === 'undefined') return 'TGI/2026/0001';
  const year = new Date().getFullYear().toString();
  const sequenceRaw = localStorage.getItem(SEQUENCE_KEY);
  let sequenceData: { year: string; seq: number } = { year, seq: 0 };
  if (sequenceRaw) {
    try {
      sequenceData = JSON.parse(sequenceRaw);
    } catch { /* ignore */ }
  }
  if (sequenceData.year !== year) {
    sequenceData = { year, seq: 0 };
  }
  sequenceData.seq += 1;
  localStorage.setItem(SEQUENCE_KEY, JSON.stringify(sequenceData));
  const padded = String(sequenceData.seq).padStart(4, '0');
  return `TGI/${year}/${padded}`;
}

export function generateId(): string {
  return `quote-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

// ── Template Storage ─────────────────────────────────────────────────────────

export function initializeTemplates(): void {
  if (typeof window === 'undefined') return;
  const initialized = localStorage.getItem(TEMPLATES_INITIALIZED_KEY);
  if (!initialized) {
    const templates: Template[] = DEFAULT_TEMPLATES.map((t) => ({
      ...t,
      isDefault: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
    localStorage.setItem(TEMPLATES_INITIALIZED_KEY, 'true');
  }
}

export function getTemplates(): Template[] {
  if (typeof window === 'undefined') return [];
  initializeTemplates();
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveTemplates(templates: Template[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
}

export function getTemplateById(id: string): Template | null {
  const templates = getTemplates();
  return templates.find((t) => t.id === id) || null;
}

export function upsertTemplate(template: Template): void {
  const templates = getTemplates();
  const idx = templates.findIndex((t) => t.id === template.id);
  if (idx >= 0) {
    templates[idx] = { ...template, updatedAt: new Date().toISOString() };
  } else {
    templates.push({ ...template, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
  }
  saveTemplates(templates);
}

export function deleteTemplate(id: string): void {
  const templates = getTemplates().filter((t) => t.id !== id);
  saveTemplates(templates);
}

export function generateTemplateId(): string {
  return `tmpl-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}
